// ── Design tokens — Shadcn (dark + light via CSS vars) ──────────────────────
// Les tokens sont exportés comme `var(--c-…)` ; les valeurs réelles sont
// définies plus bas par scope `:root[data-theme="dark|light"]`. Le swap se
// fait en CSS pur — aucun re-render React nécessaire.
//
// Le `data-theme` est posé sur <html> par le bootstrap dans public/index.html
// (anti-FOUC), puis modifié par ThemeToggle.

// Helper interne — raccourci pour `var(--name)`.
const v = (name) => `var(--${name})`;

// ── Palettes brutes (par mode) ───────────────────────────────────────────────
// Sert uniquement à générer le bloc CSS — le code applicatif passe par SHADCN.
const PALETTES = {
  dark: {
    // Surfaces
    'c-bg':            'oklch(0.145 0 0)',
    'c-fg':            'oklch(1 0 0)',
    'c-card':          'oklch(0.205 0 0 / 0.6)',
    'c-card-solid':    'oklch(0.205 0 0)',
    'c-card-fg':       'oklch(1 0 0)',
    'c-popover':       'oklch(0.205 0 0)',
    'c-popover-fg':    'oklch(1 0 0)',

    // Primary — neutre clair
    'c-primary':       'oklch(0.97 0 0)',
    'c-primary-fg':    'oklch(0.205 0 0)',
    'c-primary-light': 'oklch(0.985 0 0)',
    'c-primary-dark':  'oklch(0.97 0 0)',
    'c-primary-ring':  'oklch(0.922 0 0)',

    // Secondary / muted / accent (neutres)
    'c-secondary':     'oklch(1 0 0 / 0.1)',
    'c-secondary-fg':  'oklch(1 0 0)',
    'c-muted':         'oklch(0.269 0 0)',
    'c-muted-fg':      'oklch(0.97 0 0 / 0.7)',
    'c-accent':        'oklch(0.269 0 0)',
    'c-accent-fg':     'oklch(1 0 0)',

    // Destructive
    'c-destructive':   'oklch(0.577 0.245 27.325)',
    'c-destructive-fg':'oklch(0.704 0.191 22.216)',

    // Borders & inputs
    'c-border':        'oklch(1 0 0 / 0.125)',
    'c-input':         'oklch(1 0 0 / 0.2)',
    'c-ring':          'oklch(0.97 0 0)',

    // Sidebar
    'c-sidebar':       'oklch(0.145 0 0)',
    'c-sidebar-fg':    'oklch(1 0 0)',
    'c-sidebar-primary':    'oklch(1 0 0 / 0.85)',
    'c-sidebar-primary-fg': 'oklch(0 0 0 / 0.85)',
    'c-sidebar-accent':     'oklch(1 0 0 / 0.1)',
    'c-sidebar-accent-fg':  'oklch(1 0 0)',
    'c-sidebar-border':     'oklch(1 0 0 / 0.125)',
    'c-sidebar-ring':       'oklch(0.439 0 0)',

    // Chart (signature visuelle — identique dans les deux modes)
    'c-chart-1':       'oklch(0.865 0.127 207.078)',
    'c-chart-2':       'oklch(0.765 0.177 163.223)',
    'c-chart-3':       'oklch(0.871 0.15 154.449)',
    'c-chart-4':       'oklch(0.852 0.199 91.936)',
    'c-chart-5':       'oklch(0.685 0.169 237.323)',

    // Semantic (mappées chart — versions claires lisibles sur dark)
    'c-success':       'oklch(0.871 0.15 154.449)',
    'c-success-fg':    'oklch(0.205 0 0)',
    'c-warning':       'oklch(0.852 0.199 91.936)',
    'c-warning-fg':    'oklch(0.205 0 0)',
    'c-error':         'oklch(0.577 0.245 27.325)',
    'c-error-fg':      'oklch(1 0 0)',
    'c-info':          'oklch(0.685 0.169 237.323)',
    'c-info-fg':       'oklch(1 0 0)',
    'c-mint':          'oklch(0.871 0.15 154.449)',
    'c-mint-soft':     'oklch(0.871 0.15 154.449 / 0.12)',
    'c-mint-border':   'oklch(0.871 0.15 154.449 / 0.4)',

    // Overlays / shadows (scrims noir — OK dans les deux modes)
    'c-overlay':        'oklch(0 0 0 / 0.4)',
    'c-overlay-strong': 'oklch(0 0 0 / 0.45)',
    'c-overlay-light':  'oklch(0 0 0 / 0.32)',
    'shadow-sm': '0 1px 2px oklch(0 0 0 / 0.08)',
    'shadow-md': '0 4px 16px oklch(0 0 0 / 0.10)',
    'shadow-lg': '0 6px 16px oklch(0 0 0 / 0.40)',
    'shadow-xl': '0 10px 30px oklch(0 0 0 / 0.45)',
    'shadow-panel': '-6px 0 40px oklch(0 0 0 / 0.35)',

    // Soft tones (12 % / 40 % — calculés pour chaque couleur sémantique)
    'soft-success-bg': 'oklch(0.871 0.15 154.449 / 0.12)',
    'soft-success-border': 'oklch(0.871 0.15 154.449 / 0.4)',
    'soft-warning-bg': 'oklch(0.852 0.199 91.936 / 0.12)',
    'soft-warning-border': 'oklch(0.852 0.199 91.936 / 0.4)',
    'soft-error-bg': 'oklch(0.577 0.245 27.325 / 0.12)',
    'soft-error-border': 'oklch(0.577 0.245 27.325 / 0.4)',
    'soft-info-bg': 'oklch(0.685 0.169 237.323 / 0.12)',
    'soft-info-border': 'oklch(0.685 0.169 237.323 / 0.4)',
    'soft-chart1-bg': 'oklch(0.865 0.127 207.078 / 0.12)',
    'soft-chart1-border': 'oklch(0.865 0.127 207.078 / 0.4)',
    'soft-chart2-bg': 'oklch(0.765 0.177 163.223 / 0.12)',
    'soft-chart2-border': 'oklch(0.765 0.177 163.223 / 0.4)',
    'soft-chart3-bg': 'oklch(0.871 0.15 154.449 / 0.12)',
    'soft-chart3-border': 'oklch(0.871 0.15 154.449 / 0.4)',
    'soft-chart4-bg': 'oklch(0.852 0.199 91.936 / 0.12)',
    'soft-chart4-border': 'oklch(0.852 0.199 91.936 / 0.4)',
    'soft-chart5-bg': 'oklch(0.685 0.169 237.323 / 0.12)',
    'soft-chart5-border': 'oklch(0.685 0.169 237.323 / 0.4)',

    // Primary scale legacy (translucides clair sur dark)
    'c-primary-50':  'oklch(0.985 0 0 / 0.1)',
    'c-primary-100': 'oklch(0.985 0 0 / 0.2)',
    'c-primary-200': 'oklch(0.985 0 0 / 0.3)',
  },

  light: {
    // Surfaces — neutre clair, fond blanc cassé
    'c-bg':            'oklch(1 0 0)',
    'c-fg':            'oklch(0.145 0 0)',
    'c-card':          'oklch(1 0 0)',
    'c-card-solid':    'oklch(1 0 0)',
    'c-card-fg':       'oklch(0.145 0 0)',
    'c-popover':       'oklch(1 0 0)',
    'c-popover-fg':    'oklch(0.145 0 0)',

    // Primary — neutre sombre
    'c-primary':       'oklch(0.205 0 0)',
    'c-primary-fg':    'oklch(0.985 0 0)',
    'c-primary-light': 'oklch(0.97 0 0)',
    'c-primary-dark':  'oklch(0.145 0 0)',
    'c-primary-ring':  'oklch(0.708 0 0)',

    // Secondary / muted / accent
    'c-secondary':     'oklch(0.97 0 0)',
    'c-secondary-fg':  'oklch(0.205 0 0)',
    'c-muted':         'oklch(0.97 0 0)',
    'c-muted-fg':      'oklch(0.435 0 0)',
    'c-accent':        'oklch(0.97 0 0)',
    'c-accent-fg':     'oklch(0.205 0 0)',

    // Destructive
    'c-destructive':   'oklch(0.577 0.245 27.325)',
    'c-destructive-fg':'oklch(0.985 0 0)',

    // Borders & inputs
    'c-border':        'oklch(0 0 0 / 0.1)',
    'c-input':         'oklch(0 0 0 / 0.15)',
    'c-ring':          'oklch(0.205 0 0)',

    // Sidebar — légèrement teinté
    'c-sidebar':       'oklch(0.985 0 0)',
    'c-sidebar-fg':    'oklch(0.145 0 0)',
    'c-sidebar-primary':    'oklch(0.205 0 0)',
    'c-sidebar-primary-fg': 'oklch(0.985 0 0)',
    'c-sidebar-accent':     'oklch(0 0 0 / 0.05)',
    'c-sidebar-accent-fg':  'oklch(0.205 0 0)',
    'c-sidebar-border':     'oklch(0 0 0 / 0.08)',
    'c-sidebar-ring':       'oklch(0.708 0 0)',

    // Chart — identiques (signature)
    'c-chart-1':       'oklch(0.865 0.127 207.078)',
    'c-chart-2':       'oklch(0.765 0.177 163.223)',
    'c-chart-3':       'oklch(0.871 0.15 154.449)',
    'c-chart-4':       'oklch(0.852 0.199 91.936)',
    'c-chart-5':       'oklch(0.685 0.169 237.323)',

    // Semantic — assombris pour rester lisibles sur fond clair
    'c-success':       'oklch(0.55 0.16 154.449)',
    'c-success-fg':    'oklch(0.985 0 0)',
    'c-warning':       'oklch(0.58 0.16 75)',
    'c-warning-fg':    'oklch(0.985 0 0)',
    'c-error':         'oklch(0.577 0.245 27.325)',
    'c-error-fg':      'oklch(0.985 0 0)',
    'c-info':          'oklch(0.5 0.17 237.323)',
    'c-info-fg':       'oklch(0.985 0 0)',
    'c-mint':          'oklch(0.55 0.16 154.449)',
    'c-mint-soft':     'oklch(0.55 0.16 154.449 / 0.10)',
    'c-mint-border':   'oklch(0.55 0.16 154.449 / 0.35)',

    // Overlays / shadows
    'c-overlay':        'oklch(0 0 0 / 0.35)',
    'c-overlay-strong': 'oklch(0 0 0 / 0.45)',
    'c-overlay-light':  'oklch(0 0 0 / 0.20)',
    'shadow-sm': '0 1px 2px oklch(0 0 0 / 0.06)',
    'shadow-md': '0 4px 16px oklch(0 0 0 / 0.08)',
    'shadow-lg': '0 6px 16px oklch(0 0 0 / 0.12)',
    'shadow-xl': '0 10px 30px oklch(0 0 0 / 0.15)',
    'shadow-panel': '-6px 0 40px oklch(0 0 0 / 0.14)',

    // Soft tones — alphas légèrement augmentées pour rester perceptibles
    'soft-success-bg': 'oklch(0.55 0.16 154.449 / 0.10)',
    'soft-success-border': 'oklch(0.55 0.16 154.449 / 0.35)',
    'soft-warning-bg': 'oklch(0.58 0.16 75 / 0.12)',
    'soft-warning-border': 'oklch(0.58 0.16 75 / 0.40)',
    'soft-error-bg': 'oklch(0.577 0.245 27.325 / 0.10)',
    'soft-error-border': 'oklch(0.577 0.245 27.325 / 0.35)',
    'soft-info-bg': 'oklch(0.5 0.17 237.323 / 0.10)',
    'soft-info-border': 'oklch(0.5 0.17 237.323 / 0.35)',
    'soft-chart1-bg': 'oklch(0.55 0.13 207.078 / 0.10)',
    'soft-chart1-border': 'oklch(0.55 0.13 207.078 / 0.35)',
    'soft-chart2-bg': 'oklch(0.5 0.16 163.223 / 0.10)',
    'soft-chart2-border': 'oklch(0.5 0.16 163.223 / 0.35)',
    'soft-chart3-bg': 'oklch(0.55 0.16 154.449 / 0.10)',
    'soft-chart3-border': 'oklch(0.55 0.16 154.449 / 0.35)',
    'soft-chart4-bg': 'oklch(0.58 0.16 75 / 0.12)',
    'soft-chart4-border': 'oklch(0.58 0.16 75 / 0.40)',
    'soft-chart5-bg': 'oklch(0.5 0.17 237.323 / 0.10)',
    'soft-chart5-border': 'oklch(0.5 0.17 237.323 / 0.35)',

    // Primary scale legacy
    'c-primary-50':  'oklch(0.145 0 0 / 0.06)',
    'c-primary-100': 'oklch(0.145 0 0 / 0.10)',
    'c-primary-200': 'oklch(0.145 0 0 / 0.16)',
  },
};

// ── Exports : tokens en `var(--…)` (statique, résolu par CSS au runtime) ────
export const SHADCN = {
  // Surfaces
  bg:               v('c-bg'),
  fg:               v('c-fg'),
  card:             v('c-card'),
  cardSolid:        v('c-card-solid'),
  cardFg:           v('c-card-fg'),
  popover:          v('c-popover'),
  popoverFg:        v('c-popover-fg'),

  // Primary
  primary:          v('c-primary'),
  primaryFg:        v('c-primary-fg'),
  primaryLight:     v('c-primary-light'),
  primaryDark:      v('c-primary-dark'),
  primaryRing:      v('c-primary-ring'),

  // Secondary / muted / accent
  secondary:        v('c-secondary'),
  secondaryFg:      v('c-secondary-fg'),
  muted:            v('c-muted'),
  mutedFg:          v('c-muted-fg'),
  accent:           v('c-accent'),
  accentFg:         v('c-accent-fg'),

  // Destructive
  destructive:      v('c-destructive'),
  destructiveFg:    v('c-destructive-fg'),

  // Borders & inputs
  border:           v('c-border'),
  input:            v('c-input'),
  ring:             v('c-ring'),

  // Sidebar
  sidebar:          v('c-sidebar'),
  sidebarFg:        v('c-sidebar-fg'),
  sidebarPrimary:   v('c-sidebar-primary'),
  sidebarPrimaryFg: v('c-sidebar-primary-fg'),
  sidebarAccent:    v('c-sidebar-accent'),
  sidebarAccentFg:  v('c-sidebar-accent-fg'),
  sidebarBorder:    v('c-sidebar-border'),
  sidebarRing:      v('c-sidebar-ring'),

  // Chart
  chart1:           v('c-chart-1'),
  chart2:           v('c-chart-2'),
  chart3:           v('c-chart-3'),
  chart4:           v('c-chart-4'),
  chart5:           v('c-chart-5'),

  // Semantic
  success:          v('c-success'),
  successFg:        v('c-success-fg'),
  warning:          v('c-warning'),
  warningFg:        v('c-warning-fg'),
  error:            v('c-error'),
  errorFg:          v('c-error-fg'),
  info:             v('c-info'),
  infoFg:           v('c-info-fg'),
  mint:             v('c-mint'),

  // Brand mint translucide
  mintSoft:         v('c-mint-soft'),
  mintBorder:       v('c-mint-border'),
  accentSoft:       v('c-mint-soft'),
  accentBorder:     v('c-mint-border'),

  // Overlays & shadows
  overlay:          v('c-overlay'),
  overlayStrong:    v('c-overlay-strong'),
  overlayLight:     v('c-overlay-light'),
  shadowSm:         v('shadow-sm'),
  shadowMd:         v('shadow-md'),
  shadowLg:         v('shadow-lg'),
  shadowXl:         v('shadow-xl'),
  shadowPanel:      v('shadow-panel'),

  // Radius & fonts (invariants — non liés au mode)
  radius:           10,
  font:             "'Instrument Sans', system-ui, sans-serif",
  fontSerif:        "'Noto Serif', Georgia, serif",

  // Motion
  easeOut:          'cubic-bezier(0.23, 1, 0.32, 1)',
  easeInOut:        'cubic-bezier(0.77, 0, 0.175, 1)',
  durFast:          '120ms',
  durBase:          '180ms',
  durSlow:          '240ms',
};

// ── T — alias rétrocompatibles ──────────────────────────────────────────────
export const T = {
  // Surfaces
  paper:    SHADCN.bg,
  paper2:   SHADCN.muted,
  surface:  SHADCN.bg,
  bg:       SHADCN.bg,
  white:    SHADCN.bg,
  card:     SHADCN.cardSolid,

  // Greys textuels
  ink:      SHADCN.fg,
  ink2:     SHADCN.mutedFg,
  ink3:     SHADCN.mutedFg,
  ink4:     SHADCN.mutedFg,
  ink5:     SHADCN.border,
  text:     SHADCN.fg,
  secondary:SHADCN.mutedFg,
  muted:    SHADCN.mutedFg,
  faint:    SHADCN.mutedFg,
  subtle:   SHADCN.muted,

  // Borders
  border:   SHADCN.border,
  rule:     SHADCN.border,
  ruleInk:  SHADCN.fg,

  // Gray scale legacy
  gray25:   SHADCN.muted,
  gray50:   SHADCN.muted,
  gray100:  SHADCN.muted,
  gray200:  SHADCN.border,
  gray300:  SHADCN.border,
  gray400:  SHADCN.mutedFg,
  gray500:  SHADCN.mutedFg,
  gray600:  SHADCN.mutedFg,
  gray700:  SHADCN.fg,
  gray800:  SHADCN.fg,
  gray900:  SHADCN.fg,

  // Primary scale legacy
  primary25:   SHADCN.primaryLight,
  primary50:   v('c-primary-50'),
  primary100:  v('c-primary-100'),
  primary200:  v('c-primary-200'),
  primary300:  SHADCN.primaryLight,
  primary500:  SHADCN.primary,
  primary600:  SHADCN.primary,
  primary700:  SHADCN.primaryRing,

  // Semantic — soft scales (pointent sur les vars soft-*)
  success50:   v('soft-success-bg'),
  success200:  v('soft-success-border'),
  success500:  SHADCN.success,
  success600:  SHADCN.success,
  success700:  SHADCN.success,

  warning50:   v('soft-warning-bg'),
  warning200:  v('soft-warning-border'),
  warning500:  SHADCN.warning,
  warning600:  SHADCN.warning,
  warning700:  SHADCN.warning,

  error50:     v('soft-error-bg'),
  error200:    v('soft-error-border'),
  error500:    SHADCN.error,
  error600:    SHADCN.error,
  error700:    SHADCN.error,

  // Aliases legacy semantic
  red:       SHADCN.error,
  rouge:     SHADCN.error,
  amber:     SHADCN.warning,
  green:     SHADCN.success,
  vert:      SHADCN.success,
  vertLight: SHADCN.success,
  accent:    SHADCN.mint,
  mint:      SHADCN.mint,
  blue:      SHADCN.info,

  // Entités — neutralisées (politique "couleurs utiles uniquement").
  // L'identité d'entité passe par la typo (eyebrow, titre), pas la couleur.
  // Conservés comme alias pour rétrocompat ; pointent sur le neutre.
  cafe:   SHADCN.mutedFg,
  bureau: SHADCN.mutedFg,
  sci:    SHADCN.mutedFg,
};

// ── Espacement, radius, typo (invariants thème) ─────────────────────────────
export const S = [0, 4, 8, 12, 16, 24, 32, 48, 64];

export const R = {
  none: 0,
  xs:   4,
  sm:   6,
  md:   SHADCN.radius,
  lg:   SHADCN.radius + 2,
  xl:   SHADCN.radius + 6,
  '2xl': SHADCN.radius + 10,
  pill: 9999,
};

export const FS = {
  xs: 12, sm: 14, base: 14, md: 16, lg: 18, xl: 20, '2xl': 30, display: 48,
  meta: 12, caption: 12, body: 14, label: 14, h3: 18, h2: 20, h1: 30, hero: 48,
};

const _body = { fontFamily: SHADCN.font, fontWeight: 400, fontSize: 14, lineHeight: 1.5 };

export const TYPO = {
  display: { fontFamily: SHADCN.font, fontWeight: 600, fontSize: 52, lineHeight: 1.05, letterSpacing: '-0.035em', fontVariantNumeric: 'tabular-nums' },
  h1:      { fontFamily: SHADCN.font, fontWeight: 600, fontSize: 30, lineHeight: 1.2,  letterSpacing: '-0.025em' },
  h2:      { fontFamily: SHADCN.font, fontWeight: 600, fontSize: 20, lineHeight: 1.3,  letterSpacing: '-0.015em' },
  subhead: { fontFamily: SHADCN.font, fontWeight: 600, fontSize: 22, lineHeight: 1.1,  letterSpacing: '-0.02em' },
  h3:      { fontFamily: SHADCN.font, fontWeight: 600, fontSize: 18, lineHeight: 1.4,  letterSpacing: '-0.005em' },
  metric:  { fontFamily: SHADCN.font, fontWeight: 600, fontSize: 40, lineHeight: 1.1,  letterSpacing: '-0.025em', fontVariantNumeric: 'tabular-nums' },
  amount:  { fontFamily: SHADCN.font, fontWeight: 600, fontSize: 22, lineHeight: 1.1,  letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' },
  bodyLg:  { fontFamily: SHADCN.font, fontWeight: 400, fontSize: 16, lineHeight: 1.5 },
  titleSm: { fontFamily: SHADCN.font, fontWeight: 600, fontSize: 14, lineHeight: 1.4,  letterSpacing: '-0.005em' },
  body:    _body,
  caption: { fontFamily: SHADCN.font, fontWeight: 400, fontSize: 12, lineHeight: 1.5 },
  tag:     { fontFamily: SHADCN.font, fontWeight: 500, fontSize: 11, lineHeight: 1.4,  letterSpacing: '0.08em', textTransform: 'uppercase' },
  eyebrow: { fontFamily: SHADCN.font, fontWeight: 500, fontSize: 14, lineHeight: 1.4 },
  cta:     { fontFamily: SHADCN.font, fontWeight: 600, fontSize: 14, lineHeight: 1, letterSpacing: '-0.01em' },
  num:     { fontVariantNumeric: 'tabular-nums' },
  serif:   { fontFamily: SHADCN.fontSerif, fontWeight: 400, lineHeight: 1.3 },
};

// ── softTone() ──────────────────────────────────────────────────────────────
// Les clés du map sont les valeurs de SHADCN.* (= `var(--…)`). Les bg/border
// renvoyés sont eux-mêmes des var(--soft-…), donc auto-synchronisés au mode.
const _SOFT_MAP = {
  [SHADCN.success]: { bg: v('soft-success-bg'), border: v('soft-success-border'), text: SHADCN.success },
  [SHADCN.warning]: { bg: v('soft-warning-bg'), border: v('soft-warning-border'), text: SHADCN.warning },
  [SHADCN.error]:   { bg: v('soft-error-bg'),   border: v('soft-error-border'),   text: SHADCN.error },
  [SHADCN.info]:    { bg: v('soft-info-bg'),    border: v('soft-info-border'),    text: SHADCN.info },
  [SHADCN.chart1]:  { bg: v('soft-chart1-bg'),  border: v('soft-chart1-border'),  text: SHADCN.chart1 },
  [SHADCN.chart2]:  { bg: v('soft-chart2-bg'),  border: v('soft-chart2-border'),  text: SHADCN.chart2 },
  [SHADCN.chart3]:  { bg: v('soft-chart3-bg'),  border: v('soft-chart3-border'),  text: SHADCN.chart3 },
  [SHADCN.chart4]:  { bg: v('soft-chart4-bg'),  border: v('soft-chart4-border'),  text: SHADCN.chart4 },
  [SHADCN.chart5]:  { bg: v('soft-chart5-bg'),  border: v('soft-chart5-border'),  text: SHADCN.chart5 },
};
const _SOFT_NEUTRAL = { bg: SHADCN.card, border: SHADCN.border, text: SHADCN.mutedFg };

export function softTone(color) {
  return _SOFT_MAP[color] || _SOFT_NEUTRAL;
}

// ── Injection CSS — variables + keyframes ───────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('sonore-theme-vars')) {
  const formatBlock = (selector, palette) =>
    `${selector}{\n${Object.entries(palette).map(([k, val]) => `  --${k}: ${val};`).join('\n')}\n}`;

  // Le mode "dark" sert aussi de fallback (:root sans data-theme).
  const css =
    formatBlock(':root, :root[data-theme="dark"]', PALETTES.dark) + '\n' +
    formatBlock(':root[data-theme="light"]', PALETTES.light);

  const style = document.createElement('style');
  style.id = 'sonore-theme-vars';
  style.textContent = css;
  // Insertion en tête de <head> pour que tout autre style override puisse jouer.
  document.head.insertBefore(style, document.head.firstChild);
}

// ── Global keyframes ────────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('sonore-keyframes')) {
  const style = document.createElement('style');
  style.id = 'sonore-keyframes';
  style.textContent = `
    @keyframes sk-pulse { 0%,100% { opacity: .6 } 50% { opacity: .85 } }
    @keyframes sk-spark-draw { to { stroke-dashoffset: 0; } }
    @keyframes sk-spark-fill { to { opacity: 1; } }
    @keyframes sk-milestone {
      0%   { box-shadow: 0 0 0 0 oklch(0.871 0.15 154.449 / 0); }
      30%  { box-shadow: 0 0 0 6px oklch(0.871 0.15 154.449 / 0.18); }
      100% { box-shadow: 0 0 0 0 oklch(0.871 0.15 154.449 / 0); }
    }
    @keyframes sk-charge-paid {
      0%   { transform: scale(1); }
      40%  { transform: scale(1.18); }
      100% { transform: scale(1); }
    }
    @keyframes sk-fade-up {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes sk-check-draw {
      to { stroke-dashoffset: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      [data-sk-anim] { animation: none !important; }
    }
    @keyframes shake {
      0%,100% { transform: translateX(0); }
      20% { transform: translateX(-8px); }
      40% { transform: translateX(8px); }
      60% { transform: translateX(-6px); }
      80% { transform: translateX(6px); }
    }
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes sonore-fade-in {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    /* Press feedback */
    [data-sonore-btn]:not(:disabled):not([aria-disabled="true"]):active {
      transform: scale(0.97);
      transition: transform 90ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    [data-sonore-btn]:focus { outline: none; }
    [data-sonore-btn]:focus-visible {
      outline: 2px solid var(--c-ring);
      outline-offset: 2px;
    }
    [data-sonore-btn] { -webkit-tap-highlight-color: transparent; }
    /* Focus clavier — éléments interactifs bruts (nav, dropdowns, drawer,
       onglets de panneau) qui n'ont pas le hook [data-sonore-btn].
       :where() = spécificité 0 → un outline propre au composant l'emporte. */
    :where(button, a[href], [role="button"], select, summary):focus-visible {
      outline: 2px solid var(--c-ring);
      outline-offset: 2px;
    }
    :where(button, a[href], [role="button"]):focus:not(:focus-visible) { outline: none; }
    @media (pointer: coarse) {
      [data-sonore-btn] { position: relative; }
      [data-sonore-btn]::after {
        content: '';
        position: absolute;
        inset: -6px;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
      [data-sonore-btn]:active { transform: none !important; }
    }

    /* Logo blanc → noir en mode clair (invert la couleur, garde l'alpha). */
    :root[data-theme="light"] img[src*="logo-white"] {
      filter: invert(1);
    }

    /* Transition douce lors du swap clair/sombre — UNIQUEMENT pendant le toggle.
       La classe .theme-switching est ajoutée par applyTheme() et retirée après 300ms. */
    :root.theme-switching body,
    :root.theme-switching body * {
      transition:
        background-color 180ms cubic-bezier(0.23, 1, 0.32, 1),
        border-color    180ms cubic-bezier(0.23, 1, 0.32, 1),
        color           180ms cubic-bezier(0.23, 1, 0.32, 1);
    }
  `;
  document.head.appendChild(style);
}
